/**
 * Components of the Figma section "Web components" (docs/13 §3). Colors are the `--akm-*` tokens
 * through the Tailwind theme of `index.css`; `lg:` (1024 px) is where the desktop frames start.
 */
import { formatArtists, formatDuration, sizedImageUrl, type SongAccess } from '@akpl/shared';
import { Link } from '@tanstack/react-router';
import {
  type ButtonHTMLAttributes,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAuth } from '../../auth';
import { colorFor, cx } from '../../ui';
import autoFavorites from '../assets/auto-favorites.svg';
import autoRecent from '../assets/auto-recent.svg';
import heartOff from '../assets/heart-off.svg';
import heartOn from '../assets/heart-on.svg';
import lockIcon from '../assets/lock.svg';
import playIcon from '../assets/play.svg';
import plusIcon from '../assets/plus.svg';
import premiumLock from '../assets/premium-lock.svg';
import {
  useFavoriteIds,
  useI18nText,
  useLocale,
  useSing,
  useSongAccess,
  useToggleFavorite,
  useWebT,
} from '../hooks';
import { useWebUi, type WebSong } from '../store';

export { cx };

type ButtonVariant = 'pink' | 'surface' | 'cyan' | 'ghost' | 'outline' | 'outlinePink';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  pink: 'bg-pink text-white hover:bg-pink-strong active:bg-pink-press',
  surface: 'bg-surface-3 text-white hover:bg-surface-4',
  cyan: 'bg-cyan text-on-accent',
  ghost: 'text-white hover:bg-surface-2',
  outline: 'border border-surface-4 text-ink hover:bg-surface-2',
  outlinePink: 'border-[1.5px] border-pink text-pink hover:bg-surface-2',
};

const BUTTON_SIZES = {
  sm: 'gap-1.5 rounded-[10px] px-4 py-[9px] text-[14px]',
  md: 'gap-2 rounded-[10px] px-[18px] py-[11px] text-[14px]',
  lg: 'gap-2 rounded-[12px] px-[22px] py-[13px] text-[15px]',
  /** M/Button 46:311. */
  xl: 'h-[52px] gap-2.5 rounded-[14px] px-5 text-[16px]',
} as const;

export interface WButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: keyof typeof BUTTON_SIZES;
  block?: boolean;
}

export function WButton({
  variant = 'pink',
  size = 'lg',
  block,
  className,
  type = 'button',
  ...rest
}: WButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        'inline-flex items-center justify-center font-semibold whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    />
  );
}

export function PlayGlyph({ size = 16 }: { size?: number }) {
  return <img src={playIcon} alt="" width={size} height={size} />;
}

/** W/Badge 111:11. */
export function Badge({ type, className }: { type: SongAccess | 'new'; className?: string }) {
  const t = useWebT();
  if (type === 'premium') {
    return (
      <span
        className={cx(
          'inline-flex items-center gap-1 rounded-[6px] bg-bg px-2 py-1 text-[11px] font-bold tracking-[0.6px] whitespace-nowrap text-white',
          className,
        )}
      >
        <img src={premiumLock} alt="" width={12} height={12} />
        {t('web:common.badge.premium')}
      </span>
    );
  }
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-[6px] px-2 py-1 text-[11px] font-bold tracking-[0.6px] whitespace-nowrap',
        type === 'free' ? 'bg-cyan text-on-accent' : 'bg-pink text-white',
        className,
      )}
    >
      {t(type === 'free' ? 'web:common.badge.free' : 'web:common.badge.new')}
    </span>
  );
}

/** Square cover with the flat seed color behind it while the image loads or when there is none. */
export function Art({
  seed,
  imageUrl,
  width,
  radius = 12,
  className,
  children,
  alt = '',
}: {
  seed: string;
  imageUrl: string | null | undefined;
  /** CSS width, used to ask the media worker for the right thumbnail. */
  width: number;
  radius?: number;
  className?: string;
  children?: ReactNode;
  alt?: string;
}) {
  const src = sizedImageUrl(imageUrl, width, 2);
  return (
    <div
      className={cx('relative aspect-square shrink-0 overflow-hidden', className)}
      style={{ borderRadius: radius, backgroundColor: colorFor(seed) }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 size-full object-cover"
        />
      ) : null}
      {children}
    </div>
  );
}

/** W/Favorite 118:1306. */
export function Favorite({
  songId,
  songTitle,
  className,
}: {
  songId: string;
  /** Names the song in the "Deshacer" notice (W-10). */
  songTitle?: string;
  className?: string;
}) {
  const t = useWebT();
  const favorites = useFavoriteIds();
  const toggle = useToggleFavorite();
  const on = favorites.has(songId);
  const label = t(on ? 'web:common.actions.favoriteRemove' : 'web:common.actions.favoriteAdd');
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        toggle(songId, on, songTitle);
      }}
      className={cx(
        'group/favorite relative inline-flex size-9 shrink-0 items-center justify-center rounded-full',
        className,
      )}
    >
      <img src={on ? heartOn : heartOff} alt="" width={22} height={22} />
      {/* The tooltip of W-10 ("tooltip quitar" 126:1914). Only where there is a pointer to hover. */}
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden -translate-x-1/2 rounded-[8px] bg-ink px-2.5 py-1.5 text-[12px] leading-[normal] font-semibold whitespace-nowrap text-on-accent lg:group-hover/favorite:block"
      >
        {label}
      </span>
    </button>
  );
}

/** W/Section header 111:27. */
export function SectionHeader({
  title,
  seeAll,
  onSeeAll,
}: {
  title: string;
  seeAll?: { to: string; params?: Record<string, string> };
  /** A row with no page of its own (Home): the action instead of a link. */
  onSeeAll?: (() => void) | undefined;
}) {
  const t = useWebT();
  return (
    <div className="flex w-full items-center justify-between gap-4">
      <h2 className="m-0 text-[18px] font-bold text-ink lg:text-[22px]">{title}</h2>
      {onSeeAll ? (
        <button
          type="button"
          onClick={onSeeAll}
          className="text-[14px] font-semibold whitespace-nowrap text-muted hover:text-ink"
        >
          {t('web:common.actions.seeAll')}
        </button>
      ) : seeAll ? (
        <Link
          to={seeAll.to as never}
          params={seeAll.params as never}
          className="text-[14px] font-semibold whitespace-nowrap text-muted hover:text-ink"
        >
          {t('web:common.actions.seeAll')}
        </Link>
      ) : null}
    </div>
  );
}

/** Opens W-04 / MW-02; on the card, the whole tile does. */
function useOpenSong(song: WebSong) {
  const openOverlay = useWebUi((s) => s.openOverlay);
  return () => openOverlay({ kind: 'song', song });
}

function artistLine(song: WebSong, locale: 'en' | 'es'): string {
  return formatArtists(song.artists, locale);
}

/** W/Song card 111:17: the play button appears on hover (hidden in the frames). */
export function SongCard({
  song,
  size = 200,
  className,
}: {
  song: WebSong;
  size?: number;
  className?: string;
}) {
  const locale = useLocale();
  const t = useWebT();
  const { badge } = useSongAccess(song);
  const open = useOpenSong(song);
  const sing = useSing();
  return (
    <div className={cx('group flex min-w-0 flex-col gap-2.5', className)} style={{ width: size }}>
      <div className="relative w-full">
        <button
          type="button"
          onClick={open}
          className="block w-full text-left"
          aria-label={song.title}
        >
          <Art seed={song.id} imageUrl={song.imageUrl} width={size} className="w-full">
            {badge ? <Badge type={badge} className="absolute top-2.5 left-2.5" /> : null}
          </Art>
        </button>
        <button
          type="button"
          aria-label={t('web:common.actions.sing')}
          onClick={() => void sing(song)}
          className="absolute right-2.5 bottom-2.5 hidden size-11 items-center justify-center rounded-full bg-pink opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 lg:flex"
        >
          <PlayGlyph size={22} />
        </button>
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="m-0 truncate text-[16px] font-semibold text-ink">{song.title}</p>
        <p className="m-0 truncate text-[13px] text-muted">{artistLine(song, locale)}</p>
      </div>
    </div>
  );
}

/** W/Playlist card 111:12. */
export function PlaylistCard({
  playlist,
  size = 200,
  className,
}: {
  playlist: {
    id: string;
    name: { en: string; es: string };
    imageUrl: string | null;
    total: number;
    kind?: string;
    artistId?: string | null;
  };
  size?: number;
  className?: string;
}) {
  const t = useWebT();
  const text = useI18nText();
  const name = text(playlist.name);
  return (
    <Link
      to="/lista/$id"
      params={{ id: playlist.id }}
      className={cx('flex min-w-0 flex-col gap-2.5', className)}
      style={{ width: size }}
    >
      <Art seed={playlist.id} imageUrl={playlist.imageUrl} width={size} className="w-full" />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-[16px] font-semibold text-ink">{name}</span>
        <span className="text-[13px] text-muted">
          {t('web:common.counts.songs', { count: playlist.total })}
        </span>
      </span>
    </Link>
  );
}

/** W/Auto playlist card 118:1299: Favoritas and Recientes. */
export function AutoPlaylistCard({
  kind,
  title,
  subtitle,
  coverLabel,
  to,
  size = 200,
}: {
  kind: 'favorites' | 'recent';
  title: string;
  subtitle: string;
  coverLabel: string;
  to: string;
  size?: number;
}) {
  return (
    <Link
      to={to as never}
      className="flex min-w-0 flex-col gap-2 leading-[normal] lg:gap-2.5"
      style={{ width: size }}
    >
      {/* Proportions of the 200 px cover in 118:1299: the phone draws the same card smaller. */}
      <span
        className={cx(
          'relative block aspect-square w-full overflow-hidden rounded-[12px]',
          kind === 'favorites' ? 'bg-pink' : 'bg-surface-3',
        )}
      >
        <img
          src={kind === 'favorites' ? autoFavorites : autoRecent}
          alt=""
          className="absolute top-1/4 left-[32%] size-[36%]"
        />
        <span
          className="absolute inset-x-3 text-center font-bold text-white"
          style={{ top: kind === 'favorites' ? '70%' : '64%', fontSize: size * 0.09 }}
        >
          {coverLabel}
        </span>
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-[13px] font-semibold text-ink lg:text-[16px]">{title}</span>
        <span className="truncate text-[11px] text-muted lg:text-[13px]">{subtitle}</span>
      </span>
    </Link>
  );
}

/**
 * W/Song row 111:31 on desktop, M/Song row 95:1107 on a phone. Free: "Cantar" in pink. Premium
 * without a plan: "Desbloquear", which opens the paywall. With Premium: "Cantar", no badge.
 */
export function SongRow({
  song,
  index,
  subtitle,
  meta,
  trailing,
  showQueue = false,
  showFavorite = true,
}: {
  song: WebSong;
  /** 1-based position shown on the left, on desktop. */
  index?: number;
  subtitle?: string;
  /** Replaces the duration ("Hoy", "Ayer" in W-10b). */
  meta?: string;
  /** Extra control before the favorite ("✕" in the history). */
  trailing?: ReactNode;
  showQueue?: boolean;
  showFavorite?: boolean;
}) {
  const t = useWebT();
  const locale = useLocale();
  const { badge, outcome } = useSongAccess(song);
  const sing = useSing();
  const openOverlay = useWebUi((s) => s.openOverlay);
  const auth = useAuth();
  const locked = outcome === 'paywall' || outcome === 'signup-paywall';
  const line = subtitle ?? artistLine(song, locale);
  const open = () => openOverlay({ kind: 'song', song });
  return (
    <div
      data-testid="song-row"
      className="flex w-full items-center gap-3 rounded-[14px] bg-surface px-3 py-2.5 lg:gap-4 lg:rounded-[12px] lg:px-4 lg:hover:bg-surface-3"
    >
      {index !== undefined ? (
        <span className="hidden w-5 shrink-0 text-center text-[14px] font-semibold text-muted lg:block">
          {index}
        </span>
      ) : null}
      <button type="button" onClick={open} className="shrink-0" aria-label={song.title}>
        <Art
          seed={song.id}
          imageUrl={song.imageUrl}
          width={56}
          radius={9}
          className="size-14 lg:size-[52px] lg:!rounded-[8px]"
        />
      </button>
      <button
        type="button"
        onClick={open}
        className="flex min-w-0 flex-1 flex-col gap-0.5 text-left"
      >
        <span className="truncate text-[16px] font-semibold text-ink">{song.title}</span>
        <span className="truncate text-[13px] text-muted">{line}</span>
      </button>

      {/* Phone: lock or badge, then the round "+". */}
      {badge === 'free' ? <Badge type="free" className="shrink-0" /> : null}
      {badge === 'premium' ? (
        <>
          <Badge type="premium" className="shrink-0 max-lg:hidden" />
          <img src={lockIcon} alt="" width={20} height={20} className="mx-1 lg:hidden" />
        </>
      ) : null}

      <span className="hidden shrink-0 text-[13px] text-muted lg:inline">
        {meta ?? formatDuration(song.durationMs)}
      </span>
      {trailing}
      {showFavorite ? (
        <Favorite songId={song.id} songTitle={song.title} className="max-lg:hidden" />
      ) : null}
      {showQueue && auth.isAccount ? (
        <WButton
          variant="outline"
          size="sm"
          className="!px-3.5 max-lg:hidden"
          onClick={() => void sing(song)}
        >
          {t('web:common.actions.addToQueue')}
        </WButton>
      ) : null}
      <WButton
        size="sm"
        variant={locked ? 'surface' : 'pink'}
        className="max-lg:hidden"
        onClick={() => void sing(song)}
        data-testid="song-row-sing"
      >
        <PlayGlyph />
        {t(locked ? 'web:common.actions.unlock' : 'web:common.actions.sing')}
      </WButton>
      <button
        type="button"
        onClick={open}
        aria-label={t('web:common.actions.sing')}
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-pink lg:hidden"
      >
        <img src={plusIcon} alt="" width={18} height={18} />
      </button>
    </div>
  );
}

/** E-10: skeleton blocks, never a page spinner (§5.9). */
export function Skeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      aria-hidden
      style={style}
      className={cx('animate-pulse rounded-[12px] bg-surface-2', className)}
    />
  );
}

export function RowSkeleton({ count = 6, size = 200 }: { count?: number; size?: number }) {
  return (
    <div className="flex gap-3 overflow-hidden lg:gap-[28.8px]">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex shrink-0 flex-col gap-2.5" style={{ width: size }}>
          <Skeleton className="aspect-square w-full" />
          <Skeleton className="h-4 w-3/4 rounded-[6px]" />
          <Skeleton className="h-3 w-1/2 rounded-[6px]" />
        </div>
      ))}
    </div>
  );
}

/** Horizontal strip: scrolls on a phone and on a narrow desktop, snaps on touch. */
function StripArrow({
  direction,
  onClick,
}: {
  direction: 'previous' | 'next';
  onClick: () => void;
}) {
  const t = useWebT();
  const previous = direction === 'previous';
  return (
    <button
      type="button"
      aria-label={t(previous ? 'web:common.strip.previous' : 'web:common.strip.next')}
      data-testid={`strip-${direction}`}
      onClick={onClick}
      className={cx(
        'absolute top-[100px] z-10 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full bg-surface-3 text-ink opacity-0 shadow-lg transition-opacity hover:bg-surface-4 focus-visible:opacity-100 group-hover/strip:opacity-100 lg:flex',
        previous ? '-left-5' : '-right-5',
      )}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d={previous ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7'}
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/** "Ver todo" of a row with no list of its own: the next page of the row, then back to its start. */
export function advanceStrip(scroller: HTMLElement | null): void {
  if (!scroller) return;
  const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
  if (atEnd) scroller.scrollTo?.({ left: 0 });
  else scroller.scrollBy?.({ left: scroller.clientWidth });
}

function useStripEnds(
  ref: RefObject<HTMLDivElement | null>,
  content: ReactNode,
  onOverflowChange: ((overflows: boolean) => void) | undefined,
) {
  const [ends, setEnds] = useState({ previous: false, next: false });

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const measure = () => {
      const previous = node.scrollLeft > 1;
      const next = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
      setEnds((old) => (old.previous === previous && old.next === next ? old : { previous, next }));
      onOverflowChange?.(previous || next);
    };
    measure();
    node.addEventListener('scroll', measure, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(node);
    return () => {
      node.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [content, onOverflowChange, ref]);

  return ends;
}

export function Strip({
  children,
  className,
  scrollerRef,
  onOverflowChange,
}: {
  children: ReactNode;
  className?: string;
  scrollerRef?: RefObject<HTMLDivElement | null>;
  /** Whether the row holds more than fits, for a "Ver todo" that would otherwise do nothing. */
  onOverflowChange?: (overflows: boolean) => void;
}) {
  const own = useRef<HTMLDivElement>(null);
  const ref = scrollerRef ?? own;
  const ends = useStripEnds(ref, children, onOverflowChange);

  const page = (direction: 1 | -1) => {
    const node = ref.current;
    node?.scrollBy?.({ left: direction * node.clientWidth });
  };

  return (
    <div className="group/strip relative w-full min-w-0">
      {ends.previous ? <StripArrow direction="previous" onClick={() => page(-1)} /> : null}
      <div
        ref={ref}
        data-testid="strip"
        className={cx(
          // 28.8: six 200 px cards fill the 1344 px of W-01 exactly.
          'flex w-full snap-x gap-2.5 overflow-x-auto [scrollbar-width:none] motion-safe:scroll-smooth lg:gap-[28.8px] [&>*]:shrink-0 [&>*]:snap-start',
          className,
        )}
      >
        {children}
      </div>
      {ends.next ? <StripArrow direction="next" onClick={() => page(1)} /> : null}
    </div>
  );
}
